import { useRef, useState } from "react";
import { Alert } from "./alert";
import { Button } from "../button/button";
import { FeaturedIcon } from "../featuredIcon/featuredIcon";
import WarningIcon from "@icons/warningIcon.svg";
import styles from "./useAlert.module.scss";

export function useAlert() {
  const [alertVisible, setAlertVisible] = useState(false);
  const options = useRef<AlertOptions | undefined>();

  const iconThemes: Record<string, "red" | "primary" | "success" | "gray"> = {
    alert: "red",
    success: "success",
    info: "primary",
  };

  const primaryButtonClasses: Record<string, string> = {
    alert: styles["alertButton"],
    success: styles["successButton"],
    info: styles["infoButton"],
  };

  const showAlert = (_options: AlertOptions) => {
    options.current = _options;
    setAlertVisible(true);
  };

  const hideAlert = () => {
    setAlertVisible(false);
    options.current?.onConfirm && options.current?.onConfirm();
    options.current = undefined;
  };

  const cancelAlert = () => {
    setAlertVisible(false);
    options.current?.onCancel && options.current?.onCancel();
    options.current = undefined;
  };

  const Icon = options.current?.icon || WarningIcon;

  const AlertComponent = () => {
    return (
      <Alert
        orientation="vertical"
        title={options.current?.title}
        description={options.current?.desc}
        actionNode={
          options.current?.okayLabel && (
            <Button
              className={
                primaryButtonClasses[options.current?.theme || "alert"]
              }
              onClick={hideAlert}
            >
              {options.current?.okayLabel}
            </Button>
          )
        }
        cancelNode={
          options.current?.cancelLabel && (
            <Button className={styles["secondaryButton"]} onClick={cancelAlert}>
              {options.current?.cancelLabel}
            </Button>
          )
        }
        iconNode={
          <FeaturedIcon
            size="lg"
            color={iconThemes[options.current?.theme || "alert"]}
            theme="light-circle-outline"
          >
            <Icon />
          </FeaturedIcon>
        }
        open={alertVisible}
      ></Alert>
    );
  };

  return { showAlert, hideAlert, AlertComponent };
}

export interface AlertOptions {
  title?: string;
  desc?: string;
  cancelLabel?: string;
  okayLabel?: string;
  theme?: "alert" | "success" | "info";
  onConfirm?: () => void;
  onCancel?: () => void;
  icon?: JSX.ElementType;
}
